import React from 'react';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const DefenseUseCase = () => {
  const keyScenarios = [
    "Multi-domain coordination with Byzantine fault tolerance",
    "Tactical edge operations with degraded network connectivity",
    "Secure C4ISR orchestration with human-in-the-loop mission control"
  ];

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
      <div className="max-w-7xl mx-auto">
        <div 
          className="rounded-xl p-8 md:p-12"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Left Column - Text Content */}
            <div className="flex flex-col justify-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
                style={{ backgroundColor: 'rgba(156, 163, 175, 0.15)', border: '1px solid rgba(156, 163, 175, 0.4)', color: '#9CA3AF' }}
              >
                Defense & Dual-Use
              </div>

              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#E8EDDF' }}>
                Defense & Dual-Use
              </h2>

              <p className="text-base leading-relaxed mb-8" style={{ color: '#9CA3AF' }}>
                Multi-domain orchestration across air, land, sea, and cyber domains. Resilient operations under degraded connectivity with tactical edge computing, secure command coordination, and adaptive mission planning.
              </p>

              <div className="mb-8">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#9CA3AF' }}>
                  Key Scenarios
                </h3>
                <ul className="space-y-4">
                  {keyScenarios.map((scenario, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div 
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ backgroundColor: '#4FB3B8' }}
                      ></div>
                      <span className="text-base" style={{ color: '#E8EDDF' }}>{scenario}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link 
                to="/use-cases/defense-dual-use"
                className="inline-flex items-center gap-2 text-base font-medium transition-colors duration-250 w-fit"
                style={{ color: '#9CA3AF' }}
              >
                View Defense & Dual-Use Use Case
                <Icon name="ArrowRight" size={16} style={{ color: '#9CA3AF' }} />
              </Link>
            </div>

            {/* Right Column - Diagram */}
            <div className="flex items-center justify-center">
              <div 
                className="w-full rounded-lg p-4 relative overflow-hidden"
                style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
              >
                {/* Grid Pattern Background */}
                <div 
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: `
                      linear-gradient(rgba(79, 179, 184, 0.3) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(79, 179, 184, 0.3) 1px, transparent 1px)
                    `,
                    backgroundSize: '25px 25px'
                  }}
                ></div>
                
                <svg width="100%" height="380" viewBox="0 0 420 380" className="max-w-full relative z-10">
                  
                  {/* Top Row - Air Domain, Land Domain, Sea Domain */}
                  <rect x="30" y="20" width="100" height="45" rx="6" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="80" y="47" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">AIR DOMAIN</text>
                  
                  <rect x="160" y="20" width="100" height="45" rx="6" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="210" y="47" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">LAND DOMAIN</text>
                  
                  <rect x="290" y="20" width="100" height="45" rx="6" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="340" y="47" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">SEA DOMAIN</text>
                  
                  {/* Connection lines from top row - bracket style */}
                  <line x1="80" y1="65" x2="80" y2="85" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="210" y1="65" x2="210" y2="85" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="340" y1="65" x2="340" y2="85" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Horizontal line connecting vertical lines */}
                  <line x1="80" y1="85" x2="340" y2="85" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Center vertical line to Orchestration */}
                  <line x1="210" y1="85" x2="210" y2="115" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Tactical Edge Orchestration Box */}
                  <rect x="55" y="115" width="310" height="90" rx="6" fill="#242423" stroke="#F5CB5C" strokeWidth="2" />
                  <text x="210" y="145" textAnchor="middle" fill="#F5CB5C" fontSize="12" fontWeight="700">TACTICAL EDGE ORCHESTRATION</text>
                  <text x="210" y="170" textAnchor="middle" fill="#9CA3AF" fontSize="8">MULTI-DOMAIN COORDINATION • RESILIENT MESSAGING</text>
                  <text x="210" y="185" textAnchor="middle" fill="#9CA3AF" fontSize="8">BYZANTINE FAULT TOLERANCE • SECURE COMMAND</text>
                  
                  {/* Degraded Network Badge - Left, outside the box */}
                  <rect x="5" y="140" width="45" height="40" rx="4" fill="#FF6B6B" />
                  <circle cx="27" cy="155" r="8" fill="#242423" />
                  <text x="27" y="159" textAnchor="middle" fill="#FF6B6B" fontSize="10" fontWeight="bold">!</text>
                  <text x="27" y="172" textAnchor="middle" fill="#242423" fontSize="5" fontWeight="600">DEGRADED</text>
                  <text x="27" y="178" textAnchor="middle" fill="#242423" fontSize="5" fontWeight="600">NETWORK</text>
                  
                  {/* Line from Degraded Network to box */}
                  <line x1="50" y1="160" x2="55" y2="160" stroke="#FF6B6B" strokeWidth="2" />
                  
                  {/* Human-in-the-Loop Badge - Right, outside the box */}
                  <rect x="370" y="140" width="45" height="40" rx="4" fill="#4FB3B8" />
                  <circle cx="392" cy="155" r="8" fill="#242423" />
                  <text x="392" y="159" textAnchor="middle" fill="#4FB3B8" fontSize="10">●</text>
                  <text x="392" y="172" textAnchor="middle" fill="#242423" fontSize="5" fontWeight="600">HUMAN-IN-</text>
                  <text x="392" y="178" textAnchor="middle" fill="#242423" fontSize="5" fontWeight="600">THE-LOOP</text>
                  
                  {/* Line from box to Human-in-the-Loop */}
                  <line x1="365" y1="160" x2="370" y2="160" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Connection lines from Orchestration to bottom - bracket style */}
                  <line x1="210" y1="205" x2="210" y2="235" stroke="#9CA3AF" strokeWidth="2" />
                  <line x1="100" y1="235" x2="320" y2="235" stroke="#9CA3AF" strokeWidth="2" />
                  
                  {/* Vertical lines to bottom circles */}
                  <line x1="100" y1="235" x2="100" y2="265" stroke="#9CA3AF" strokeWidth="2" />
                  <line x1="210" y1="235" x2="210" y2="265" stroke="#9CA3AF" strokeWidth="2" />
                  <line x1="320" y1="235" x2="320" y2="265" stroke="#9CA3AF" strokeWidth="2" />
                  
                  {/* Bottom Row - C2, C4I, ISR circles */}
                  <circle cx="100" cy="310" r="45" fill="#242423" stroke="#9CA3AF" strokeWidth="2" />
                  <text x="100" y="315" textAnchor="middle" fill="#E8EDDF" fontSize="16" fontWeight="600">C2</text>
                  
                  <circle cx="210" cy="310" r="45" fill="#242423" stroke="#9CA3AF" strokeWidth="2" />
                  <text x="210" y="315" textAnchor="middle" fill="#E8EDDF" fontSize="16" fontWeight="600">C4I</text>
                  
                  <circle cx="320" cy="310" r="45" fill="#242423" stroke="#9CA3AF" strokeWidth="2" />
                  <text x="320" y="315" textAnchor="middle" fill="#E8EDDF" fontSize="16" fontWeight="600">ISR</text>
                  
                </svg>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default DefenseUseCase;